/**
 * Students Page - Manage classes and students
 *
 * Local SQLite database management for student names.
 * Names stored locally only - never sent to server.
 */

import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLocalDbStore } from "@/store/localDbStore";
import {
  DatabaseBanner,
  ClassList,
  ClassDialog,
  StudentTable,
  StudentDialog,
  BulkImportDialog,
  PasswordDialog,
} from "@/components/students";
import { Users, Shield, Pencil, Trash2 } from "lucide-react";
import type { LocalClass, LocalStudent, CreateClassInput, UpdateStudentInput } from "@/types/local-db.types";

export default function Students() {
  // Store state
  const {
    isConnected,
    isDirty,
    selectedClassId,
    students,
    classes,
    loading,
    error,
    pendingFileCheck,
    failedPasswordAttempts,
    addClass,
    editClass,
    removeClass,
    addStudent,
    editStudent,
    removeStudent,
    importStudents,
    openEncryptedDatabase,
    clearPendingFileCheck,
    clearPassword,
  } = useLocalDbStore();

  // ==========================================================================
  // Beforeunload handler for unsaved changes and password cleanup
  // ==========================================================================

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Clear password from memory when tab closes (security)
      clearPassword();

      if (isConnected && isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isConnected, clearPassword]);

  // ==========================================================================
  // Password dialog state for encrypted databases
  // ==========================================================================

  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Show password dialog when we have a pending encrypted file
  const showPasswordDialog = !!pendingFileCheck?.isEncrypted;

  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      setPasswordError(null);
      try {
        await openEncryptedDatabase(password);
      } catch (err) {
        setPasswordError(
          err instanceof Error ? err.message : "Incorrect password"
        );
      }
    },
    [openEncryptedDatabase]
  );

  const handlePasswordDialogClose = useCallback(
    (open: boolean) => {
      if (!open) {
        clearPendingFileCheck();
        setPasswordError(null);
      }
    },
    [clearPendingFileCheck]
  );

  // Dialog state
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<LocalClass | null>(null);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<LocalStudent | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Delete confirmation state
  const [deleteClassId, setDeleteClassId] = useState<string | null>(null);
  const [deleteStudent, setDeleteStudent] = useState<LocalStudent | null>(null);

  // Get selected class details
  const selectedClass = classes.find((c) => c.id === selectedClassId);

  // Calculate next sheet number
  const nextSheetNumber = students.length > 0
    ? String(Math.max(...students.map((s) => parseInt(s.sheetNumber))) + 1)
    : "1";

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleAddClass = useCallback(() => {
    setEditingClass(null);
    setClassDialogOpen(true);
  }, []);

  const handleEditClass = useCallback(() => {
    if (selectedClass) {
      setEditingClass(selectedClass);
      setClassDialogOpen(true);
    }
  }, [selectedClass]);

  const handleClassSubmit = useCallback(
    (data: CreateClassInput) => {
      try {
        if (editingClass) {
          editClass(editingClass.id, data);
          toast.success("Class updated successfully");
        } else {
          addClass(data);
          toast.success("Class created successfully");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to save class";
        toast.error(message);
      }
    },
    [editingClass, addClass, editClass]
  );

  const handleDeleteClass = useCallback(() => {
    if (deleteClassId) {
      try {
        removeClass(deleteClassId);
        setDeleteClassId(null);
        toast.success("Class deleted successfully");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to delete class";
        toast.error(message);
        setDeleteClassId(null);
      }
    }
  }, [deleteClassId, removeClass]);

  const handleAddStudent = useCallback(() => {
    setEditingStudent(null);
    setStudentDialogOpen(true);
  }, []);

  const handleEditStudentClick = useCallback((student: LocalStudent) => {
    setEditingStudent(student);
    setStudentDialogOpen(true);
  }, []);

  const handleStudentSubmit = useCallback(
    (data: { name: string; sheetNumber?: string }) => {
      if (!selectedClassId) return;

      try {
        if (editingStudent) {
          const updates: UpdateStudentInput = {};
          if (data.name !== editingStudent.name) updates.name = data.name;
          if (data.sheetNumber !== editingStudent.sheetNumber) {
            updates.sheetNumber = data.sheetNumber;
          }
          editStudent(editingStudent.id, updates);
          toast.success("Student updated successfully");
        } else {
          addStudent(selectedClassId, data.name, data.sheetNumber || undefined);
          toast.success("Student added successfully");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to save student";
        toast.error(message);
      }
    },
    [selectedClassId, editingStudent, addStudent, editStudent]
  );

  const handleDeleteStudent = useCallback(() => {
    if (deleteStudent) {
      try {
        removeStudent(deleteStudent.id);
        setDeleteStudent(null);
        toast.success("Student removed successfully");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to remove student";
        toast.error(message);
        setDeleteStudent(null);
      }
    }
  }, [deleteStudent, removeStudent]);

  const handleImport = useCallback(
    (studentsData: Array<{ name: string; sheetNumber?: string }>) => {
      if (selectedClassId) {
        try {
          importStudents(selectedClassId, studentsData);
          toast.success(`Imported ${studentsData.length} student${studentsData.length !== 1 ? "s" : ""} successfully`);
        } catch (error) {
          const message = error instanceof Error ? error.message : "Failed to import students";
          toast.error(message);
        }
      }
    },
    [selectedClassId, importStudents]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Students</h1>
        <p className="text-muted-foreground">
          Manage your classes and students. Names are stored locally on your device.
        </p>
      </div>

      {/* Database Banner */}
      <DatabaseBanner />

      {/* Privacy Notice */}
      {!isConnected && (
        <Card className="bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <Shield className="h-5 w-5" />
              Privacy by Design
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-green-700/80 dark:text-green-400/80">
              Student names are stored in a local database file on your computer.
              They are <strong>never</strong> uploaded to our servers. When you scan
              worksheets, we only see sheet numbers (1-999), not student names.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {isConnected && (
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          {/* Sidebar - Class List */}
          <Card className="lg:h-[600px]">
            <ClassList onAddClass={handleAddClass} />
          </Card>

          {/* Main Content - Students */}
          <Card>
            {selectedClass ? (
              <>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      {selectedClass.name}
                    </CardTitle>
                    <CardDescription>
                      {selectedClass.grade && `Grade ${selectedClass.grade} • `}
                      {students.length} student{students.length !== 1 ? "s" : ""}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditClass}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit Class
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteClassId(selectedClass.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <StudentTable
                    students={students}
                    onAddStudent={handleAddStudent}
                    onEditStudent={handleEditStudentClick}
                    onDeleteStudent={(s) => setDeleteStudent(s)}
                    onImport={() => setImportDialogOpen(true)}
                  />
                </CardContent>
              </>
            ) : (
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  Select a class from the sidebar to view students
                </p>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Dialogs */}
      <PasswordDialog
        open={showPasswordDialog}
        onOpenChange={handlePasswordDialogClose}
        mode="unlock"
        onSubmit={handlePasswordSubmit}
        error={passwordError || (error && pendingFileCheck ? error : null)}
        fileName={pendingFileCheck?.fileName}
        loading={loading}
        failedAttempts={failedPasswordAttempts}
      />

      <ClassDialog
        open={classDialogOpen}
        onOpenChange={setClassDialogOpen}
        editingClass={editingClass}
        onSubmit={handleClassSubmit}
      />

      <StudentDialog
        open={studentDialogOpen}
        onOpenChange={setStudentDialogOpen}
        editingStudent={editingStudent}
        nextSheetNumber={nextSheetNumber}
        onSubmit={handleStudentSubmit}
      />

      <BulkImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImport}
        existingSheetNumbers={students.map((s) => s.sheetNumber)}
      />

      {/* Delete Class Confirmation */}
      <AlertDialog
        open={!!deleteClassId}
        onOpenChange={(open: boolean) => !open && setDeleteClassId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Class?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this class and all its students.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClass}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Student Confirmation */}
      <AlertDialog
        open={!!deleteStudent}
        onOpenChange={(open: boolean) => !open && setDeleteStudent(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {deleteStudent?.name} from this class.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStudent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
